import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { TimeInput24 } from "@/components/TimeInput24";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const TemplateEditor = ({ t, onSaved }) => {
  const [f, setF] = useState(t);
  const setReq = (i, p) => setF({ ...f, requirements: f.requirements.map((r, j) => (j === i ? { ...r, ...p } : r)) });
  const save = async () => {
    try {
      const body = { name: f.name, start: f.start, end: f.end, requirements: f.requirements.map((r) => ({ ...r, key: r.key || r.label.toLowerCase().replace(/[^a-z0-9]+/g, "_") })), days: f.days, active: f.active };
      const { data } = f.id ? await api.patch(`/rota/templates/${f.id}`, body) : await api.post("/rota/templates", body);
      toast.success("Shift saved");
      onSaved(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3 rounded-xl border p-4" data-testid={`template-${f.id || "new"}`}>
      <div className="flex flex-wrap gap-2">
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Shift name" className="min-w-[140px] flex-1" data-testid="template-name" />
        <TimeInput24 value={f.start} onChange={(v) => setF({ ...f, start: v })} testid="template-start" />
        <span className="self-center text-xs text-muted-foreground">to</span>
        <TimeInput24 value={f.end} onChange={(v) => setF({ ...f, end: v })} testid="template-end" />
        <label className="flex items-center gap-2 text-xs"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /> In use</label>
      </div>
      <div className="flex flex-wrap gap-1">
        {DAYS.map((d, i) => <button key={d} type="button" onClick={() => setF({ ...f, days: f.days.includes(i) ? f.days.filter((x) => x !== i) : [...f.days, i] })} className={`rounded-full border px-3 py-1 text-xs ${f.days.includes(i) ? "border-primary bg-primary/15 text-primary" : "text-muted-foreground"}`}>{d}</button>)}
      </div>
      {f.requirements.map((r, i) => (
        <div key={i} className="flex gap-2">
          <Input value={r.label} onChange={(e) => setReq(i, { label: e.target.value, key: r.key })} placeholder="Role e.g. Rider" className="flex-1" />
          <Input type="number" min={1} value={r.count} onChange={(e) => setReq(i, { count: Number(e.target.value) })} className="w-20" />
          <button onClick={() => setF({ ...f, requirements: f.requirements.filter((_, j) => j !== i) })} className="p-2 text-red-400" aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <div className="flex gap-2">
        <button onClick={() => setF({ ...f, requirements: [...f.requirements, { key: "", label: "", count: 1 }] })} className="rounded-full border px-3 py-1.5 text-xs font-semibold"><Plus className="mr-1 inline h-3 w-3" />Role</button>
        <button onClick={save} data-testid="template-save" className="rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground">Save shift</button>
      </div>
    </div>
  );
};

export const RotaSetupDialog = ({ setup, onClose, onChange }) => {
  const [areaName, setAreaName] = useState("");
  const addArea = async () => {
    try { await api.post("/rota/areas", { name: areaName, active: true }); setAreaName(""); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleArea = async (a) => { await api.patch(`/rota/areas/${a.id}`, { name: a.name, active: !a.active }); onChange(); };
  const blank = { name: "", start: "07:00", end: "13:00", days: [0, 1, 2, 3, 4, 5, 6], active: true, requirements: [{ key: "rider", label: "Rider", count: 2 }] };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl" data-testid="rota-setup-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Rota setup</DialogTitle></DialogHeader>
        <h4 className="font-bold">Areas</h4>
        <div className="flex flex-wrap gap-2">
          {setup.areas.map((a) => <button key={a.id} onClick={() => toggleArea(a)} data-testid={`area-toggle-${a.id}`} className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${a.active ? "border-primary text-primary" : "text-muted-foreground line-through"}`}>{a.name}</button>)}
        </div>
        <div className="flex gap-2"><Input placeholder="New area" value={areaName} onChange={(e) => setAreaName(e.target.value)} data-testid="new-area-input" /><button onClick={addArea} disabled={!areaName} data-testid="new-area-btn" className="rounded-full bg-primary px-4 text-sm font-bold text-primary-foreground disabled:opacity-40">Add</button></div>
        <h4 className="pt-2 font-bold">Shifts (apply to every area)</h4>
        {setup.templates.map((t) => <TemplateEditor key={t.id} t={t} onSaved={onChange} />)}
        <TemplateEditor key={setup.templates.length} t={blank} onSaved={onChange} />
      </DialogContent>
    </Dialog>
  );
};
